import {
  SalesOrderSortOption,
  SalesOrderStatus,
} from '@rematter/frontend-lib-api-client';

import { SalesOrderRecord } from '../types/salesOrder';
import { getSalesOrderLineItemsTotals } from '../utils/weight';

import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';
import {
  maybeParseCalendarDate,
  parseZonedDateTime,
} from '~/shared/utils/dateTime';

export const salesOrdersListQueryAtom = createQueryAtomFactory(
  'trade.salesOrdersList',
  (params: {
    ids?: string[];
    searchTerm?: string;
    facilityId?: string;
    accountIds?: string[];
    createdByUserIds?: string[];
    limit?: number;
    offset?: number;
    sorting?: SalesOrderSortOption[];
    status?: SalesOrderStatus[];
    includeLineItems?: boolean;
    fromEndDate?: string;
    toEndDate?: string;
    fromCreatedAt?: string;
    toCreatedAt?: string;
  }) => ({
    salesOrders: {
      __args: {
        filter: {
          ids: params.ids,
          searchTerm: params.searchTerm,
          facilityIds: params.facilityId ? [params.facilityId] : undefined,
          status: params.status,
          includeLineItems: params.includeLineItems ?? true,
          accountIds: params.accountIds,
          createdBy: params.createdByUserIds,
          fromEndDate: params.fromEndDate,
          toEndDate: params.toEndDate,
          fromCreatedAt: params.fromCreatedAt,
          toCreatedAt: params.toCreatedAt,
        },
        sorting: params.sorting,
        pagination: {
          limit: params.limit ?? 10,
          offset: params.offset ?? 0,
        },
      },
      entries: {
        id: true,
        documentNumber: true,
        status: true,
        startDate: true,
        endDate: true,
        counterpartyReferenceNumber: true,
        weightDisplayUnit: true,
        ...(params.includeLineItems
          ? {
              lineItems: {
                unit: true,
                quantity: true,
                totalPrice: {
                  amount: true,
                  currencyCode: true,
                },
              },
            }
          : {}),
        account: {
          id: true,
          name: true,
        },
        facility: {
          id: true,
        },
        createdBy: true,
        createdByUser: {
          id: true,
          firstName: true,
          lastName: true,
        },
        timestamps: {
          createdAt: true,
          updatedAt: true,
        },
      },
      page: {
        total: true,
      },
    },
  }),
  {
    select: (
      data,
    ): {
      salesOrders: SalesOrderRecord[];
      totalCount: number;
    } => {
      // Handle undefined or null data
      if (!data || !data.salesOrders || !data.salesOrders.entries) {
        return { salesOrders: [], totalCount: 0 };
      }

      if (!data.salesOrders.entries.length) {
        return { salesOrders: [], totalCount: 0 };
      }

      return {
        salesOrders: data.salesOrders.entries.map((salesOrder) => {
          const lineItems = salesOrder.lineItems || [];

          const { totalEachCount, totalNetWeight, totalPrice, netWeight } =
            getSalesOrderLineItemsTotals(
              lineItems,
              salesOrder.weightDisplayUnit,
            );

          // Calculate shipped loads count and fulfilled percentage (mock for now)
          const shippedLoadsCount = 0; // This would need to be fetched from loads
          const fulfilledPercentage = 0; // This would need to be calculated based on loads

          return {
            id: salesOrder.id,
            documentNumber: salesOrder.documentNumber,
            status: salesOrder.status as SalesOrderStatus,
            startDate: maybeParseCalendarDate(salesOrder.startDate),
            endDate: maybeParseCalendarDate(salesOrder.endDate),
            counterpartyReferenceNumber:
              salesOrder.counterpartyReferenceNumber || undefined,
            weightDisplayUnit: salesOrder.weightDisplayUnit ?? undefined,
            lineItemCount: lineItems.length,
            netWeight,
            totalNetWeight,
            totalEachCount,
            shippedLoadsCount,
            fulfilledPercentage,
            totalPrice,
            account: salesOrder.account,
            facility: salesOrder.facility,
            customerName: salesOrder.account.name,
            createdBy: salesOrder.createdBy,
            createdByUser: salesOrder.createdByUser || null,
            timestamps: {
              createdAt: parseZonedDateTime(salesOrder.timestamps.createdAt),
              updatedAt: parseZonedDateTime(salesOrder.timestamps.updatedAt),
            },
          } as SalesOrderRecord;
        }),
        totalCount: data.salesOrders.page?.total || 0,
      };
    },
  },
);
